import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import ThresholdRangeTypes from '@avtechsoftware/ra-javascript-utilities/src/types/ThresholdRangeTypes';
import SortableTableHeader from '../../../common/SortableTableHeader/SortableTableHeader';
import ThresholdRangeTableRow from './ThresholdRangeTableRow';
import RaaThresholdRange from '../../../../classes/RaaThresholdRange';

const RaaThresholdRangeList = ({
  thresholdRangeList,
  onEdit,
  onDelete,
  onClone,
  onView,
  onASHRAERangeClick,
  onNWSRangeClick,
  temperatureScale,
  hasEditPrivileges,
  airSpeedScale,
}) => {
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [selectedSortType, setSelectedSortType] = useState(null);
  const [sortedThresholdRanges, setSortedThresholdRanges] = useState([]);
  const [sortDirection, setSortDirection] = useState(null);

  const renderName = (thresholdRange) => {
    const name = thresholdRange.typeId === ThresholdRangeTypes.PREDEFINED
      ? (
        <button
          type="button"
          className="btn btn-link p-0 pb-1"
          onClick={thresholdRange.groupId === 1
            ? onASHRAERangeClick
            : onNWSRangeClick}
        >
          {thresholdRange.name}
        </button>
      )
      : (<>{thresholdRange.name}</>);

    return (
      <>
        {name}
      </>
    );
  };

  const detailedThresholdRanges = useMemo(() => thresholdRangeList.map((thresholdRange) => {
    const thresholdRangeCopy = Object.assign(
      Object.create(Object.getPrototypeOf(thresholdRange)),
      thresholdRange,
    );
    thresholdRangeCopy.disabled = thresholdRange.typeId === ThresholdRangeTypes.PREDEFINED
        || !hasEditPrivileges;
    thresholdRangeCopy.displayValue = thresholdRangeCopy
      .getDisplayString(temperatureScale, airSpeedScale);
    thresholdRangeCopy.displayName = renderName(thresholdRange);
    return thresholdRangeCopy;
  }),
  [thresholdRangeList, hasEditPrivileges, temperatureScale, airSpeedScale]);

  const handleGetSortedList = (sort, sortType) => {
    const sortedList = [...detailedThresholdRanges].sort((a, b) => {
      const valueA = a[sortType];
      const valueB = b[sortType];

      if (valueA == null || valueB == null) return 0;

      if (typeof valueA === 'string' && typeof valueB === 'string') {
        const comparison = valueA.localeCompare(valueB, undefined, { sensitivity: 'base' });
        return sort === 'ASC' ? comparison : -comparison;
      }

      if (sort === 'ASC') {
        // eslint-disable-next-line no-nested-ternary
        return valueA > valueB ? 1 : valueA < valueB ? -1 : 0;
      }
      // eslint-disable-next-line no-nested-ternary
      return valueA < valueB ? 1 : valueA > valueB ? -1 : 0;
    });

    setSortedThresholdRanges(sortedList);
    setSelectedSortType(sortType);
    setSortDirection(sort);
  };

  React.useEffect(() => {
    if (selectedSortType && sortDirection) {
      handleGetSortedList(sortDirection, selectedSortType);
    } else {
      setSortedThresholdRanges(detailedThresholdRanges);
    }
  }, [detailedThresholdRanges]);

  const columnLabelMap = [
    { label: 'Name', sortType: 'name' },
    { label: 'Threshold / Range', sortType: 'displayValue' },
    { label: 'Used in...', sortType: 'numUsedDisplay' },
  ];

  return (
    <div className="table-responsive">
      <table className="table table-sm table-condensed table-hover table-striped my-1">
        <thead className="thead-light">
          <tr>
            {/* eslint-disable-next-line jsx-a11y/control-has-associated-label */}
            <th>{' '}</th>
            {columnLabelMap.map((col) => (
              <SortableTableHeader
                key={col.label}
                onDescClick={() => handleGetSortedList('DES', col.sortType)}
                label={col.label}
                onAscClick={() => handleGetSortedList('ASC', col.sortType)}
                isSelected={col.sortType === selectedSortType}
              />
            ))}
            {/* eslint-disable-next-line jsx-a11y/control-has-associated-label */}
            <th className="text-right" />
          </tr>
        </thead>
        <tbody>
          {sortedThresholdRanges.map((thresholdRange, index) => (
            <ThresholdRangeTableRow
              key={thresholdRange.id}
              thresholdRange={thresholdRange}
              index={index}
              hoveredIndex={hoveredIndex}
              setHoveredIndex={setHoveredIndex}
              disabled={thresholdRange.disabled}
              hasEditPrivileges={hasEditPrivileges}
              onEdit={() => onEdit(thresholdRange.id)}
              onDelete={() => onDelete(thresholdRange.id)}
              onClone={() => onClone(thresholdRange.id)}
              onView={() => onView(thresholdRange.id)}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
};

RaaThresholdRangeList.propTypes = {
  thresholdRangeList: PropTypes.arrayOf(PropTypes.shape(RaaThresholdRange)).isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onClone: PropTypes.func.isRequired,
  onView: PropTypes.func.isRequired,
  onASHRAERangeClick: PropTypes.func.isRequired,
  onNWSRangeClick: PropTypes.func.isRequired,
  temperatureScale: PropTypes.string.isRequired,
  hasEditPrivileges: PropTypes.bool.isRequired,
  airSpeedScale: PropTypes.string.isRequired,
};

export default RaaThresholdRangeList;
